import { AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { SessionStorageKeys } from "../../models/StorageKeys";
import { describeError } from "../../helpers/ErrorHelpers";
import { ConfigurationService } from "../ConfigurationService";
import { Logger } from "../Logger";
import { StorageWrapper } from "../StorageWrapper";
import {
    createSharePointMsalClient,
    SHAREPOINT_GRAPH_SCOPES,
    SharePointMsalClientOptions,
} from "./SharePointAuthService";
import {
    isValidMsalAuthority,
    isValidMsalClientId,
    isValidMsalLoginHint,
} from "../../helpers/ProjectFolderConnectionHelpers";

export interface SharePointConsentRedirectRequest {
    scopes: string[];
    clientId: string;
    authority: string;
    /** Mailbox user's email; lets Entra pick the matching session instead of showing an account picker. */
    loginHint?: string;
}

export interface SharePointConsentRedirectOutcome {
    result: AuthenticationResult | null;
    error: string | null;
}

/**
 * Both legs of the Office Dialog (MSAL-only) fallback, which run inside the dialog window:
 *
 * 1. `beginInteractiveRedirect` (from `SharePointConsentInitiateComponent`): remembers the
 *    connection's clientId/authority in the dialog window's sessionStorage, then runs
 *    `acquireTokenRedirect`, which navigates the dialog to Microsoft's login page.
 * 2. `completePendingRedirect` (from `EntryPoint`, on every non-NAA page load): if that marker is
 *    present, this load is the dialog returning from Microsoft. Rebuild the same client and let
 *    `handleRedirectPromise` finish the flow. Returns null otherwise, so normal taskpane loads never
 *    construct an MSAL client.
 *
 * The marker is needed because the redirect URI is a plain `index.html` with no project context,
 * and Entra identifiers now come from project settings rather than build-time config.
 */
export class SharePointConsentRedirectService {
    constructor(
        private storageWrapper: StorageWrapper,
        private configurationService: ConfigurationService,
        private logger: Logger,
        private readonly createMsalClient: (
            options: SharePointMsalClientOptions
        ) => IPublicClientApplication = createSharePointMsalClient
    ) {}

    async beginInteractiveRedirect(request: SharePointConsentRedirectRequest): Promise<void> {
        // The dialog page gets these from its URL, so anyone can craft them. Only start a consent prompt
        // for a GUID app on a tenant-specific Microsoft authority with this feature's fixed scope, so a
        // crafted link can't turn this origin into a consent-phishing page for an arbitrary app or
        // permission. Which app/tenant pairs are legitimate is decided on the task-pane side: the dialog
        // is only ever opened for a connection built from NPC project settings, and `messageParent` can
        // only reach the task pane that opened it. (The Office Dialog does not share the task pane's
        // storage on every host, so an allow-list cannot be checked here.)
        if (!isValidMsalClientId(request.clientId) || !isValidMsalAuthority(request.authority)) {
            throw new Error("Invalid SharePoint clientId or authority");
        }
        if (request.scopes.length === 0 || request.scopes.some((scope) => !SHAREPOINT_GRAPH_SCOPES.includes(scope))) {
            throw new Error("Invalid SharePoint consent scopes");
        }

        const options = this.toClientOptions(request);
        this.storageWrapper.saveSessionStorage(
            SessionStorageKeys.sharePointConsentPending,
            JSON.stringify({ clientId: request.clientId, authority: request.authority })
        );

        const msalClient = this.createMsalClient(options);
        this.logger.info("SharePointConsentRedirectService: MSAL step 1/2 - pca.initialize()");
        await msalClient.initialize();
        // An unusable hint only means Entra may show its account picker, so drop it rather than fail.
        const loginHint = isValidMsalLoginHint(request.loginHint) ? request.loginHint : undefined;
        if (request.loginHint && !loginHint) {
            this.logger.warning("SharePointConsentRedirectService: ignoring a malformed loginHint query param");
        }
        this.logger.info(
            `SharePointConsentRedirectService: MSAL step 2/2 - pca.acquireTokenRedirect(scopes=[${request.scopes.join(
                ", "
            )}], loginHint=${loginHint ? "set" : "none"}) - navigating this dialog to Microsoft's login page`
        );
        await msalClient.acquireTokenRedirect(
            loginHint ? { scopes: request.scopes, loginHint } : { scopes: request.scopes }
        );
    }

    async completePendingRedirect(): Promise<SharePointConsentRedirectOutcome | null> {
        const pending = this.readAndClearPending();
        if (!pending) {
            return null;
        }

        try {
            const msalClient = this.createMsalClient(this.toClientOptions(pending));
            await msalClient.initialize();
            // navigateToLoginRequestUrl: false -> process the auth-code response on this page instead of
            // MSAL navigating back to "#sharepointconsentrouting", which would re-fire acquireTokenRedirect
            // in a loop and never post a result to the parent.
            this.logger.info(
                "SharePointConsentRedirectService: handleRedirectPromise({ navigateToLoginRequestUrl: false })"
            );
            const result = await msalClient.handleRedirectPromise({ navigateToLoginRequestUrl: false });
            if (!result) {
                this.logger.error(
                    "SharePointConsentRedirectService: a consent redirect was pending but handleRedirectPromise resolved null"
                );
                return { result: null, error: "SharePoint consent redirect returned no result" };
            }
            this.logger.info("SharePointConsentRedirectService: handleRedirectPromise resolved a consent result");
            return { result, error: null };
        } catch (error) {
            this.logger.error("SharePointConsentRedirectService: handleRedirectPromise failed", error);
            return { result: null, error: describeError(error) };
        }
    }

    private readAndClearPending(): Pick<SharePointMsalClientOptions, "clientId" | "authority"> | null {
        const raw = this.storageWrapper.loadSessionStorage(SessionStorageKeys.sharePointConsentPending);
        if (!raw) {
            return null;
        }
        this.storageWrapper.removeSessionStorageItem(SessionStorageKeys.sharePointConsentPending);
        try {
            const parsed = JSON.parse(raw);
            if (isValidMsalClientId(parsed?.clientId) && isValidMsalAuthority(parsed?.authority)) {
                return { clientId: parsed.clientId, authority: parsed.authority };
            }
        } catch (error) {
            this.logger.error("SharePointConsentRedirectService: ignoring malformed pending-consent marker", error);
        }
        return null;
    }

    private toClientOptions(
        ids: Pick<SharePointMsalClientOptions, "clientId" | "authority">
    ): SharePointMsalClientOptions {
        return {
            clientId: ids.clientId,
            authority: ids.authority,
            redirectUri: this.configurationService.sharePointMsalRedirectUrl,
        };
    }
}
